
/**
 * プロジェクト詳細クライアント
 * プロジェクトの詳細情報表示とタブ管理
 */

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs"
import { useParams } from 'react-router-dom'
import { Skeleton } from "@/shared/ui/skeleton"
import { Alert, AlertDescription } from "@/shared/ui/alert"
import { AlertTriangle, RefreshCw } from "lucide-react"
import { Button } from "@/shared/ui/button"

import BasicInfoTab from '@/features/projects/components/BasicInfoTab'
import DailyActivitiesTab from '@/features/projects/components/DailyActivitiesTab'
import { useProject } from '@/features/projects/hooks/useProject'

/**
 * プロジェクト詳細クライアントコンポーネント
 */
export default function ProjectDetailClient() {
    const params = useParams()
    const projectId = params.project_id as string

    const { project, isLoading, error, mutate, updateProject } = useProject(projectId)

    if (isLoading) {
        return (
            <div className="container mx-auto px-3 py-3 sm:px-6 sm:py-6 lg:px-8">
                <div className="mb-3 md:mb-8 p-3 md:p-6 bg-muted/50 rounded-lg shadow-sm">
                    <Skeleton className="h-8 w-2/3 mb-2" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4 mt-2" />
                </div>
                <div className="space-y-4">
                    <Skeleton className="h-12 w-full" />
                    <Skeleton className="h-96 w-full" />
                </div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="container mx-auto px-3 py-3 sm:px-6 sm:py-6 lg:px-8">
                <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription className="flex items-center justify-between">
                        <span>プロジェクトの読み込みに失敗しました: {String(error)}</span>
                        <Button variant="outline" size="sm" onClick={() => mutate()}>
                            <RefreshCw className="h-4 w-4 mr-2" />
                            再試行
                        </Button>
                    </AlertDescription>
                </Alert>
            </div>
        )
    }

    if (!project) {
        return (
            <div className="container mx-auto px-3 py-3 sm:px-6 sm:py-6 lg:px-8">
                <Alert>
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>プロジェクトが見つかりません</AlertDescription>
                </Alert>
            </div>
        )
    }

    return (
        <div className="container mx-auto px-3 py-3 sm:px-6 sm:py-6 lg:px-8">
            <div className="mb-3 md:mb-8 p-3 md:p-6 bg-muted/50 rounded-lg shadow-sm">
                <h1 className="text-lg md:text-xl font-semibold text-foreground">
                    {project.name}
                </h1>
                <p className="mt-1 md:mt-2 text-sm md:text-lg text-muted-foreground sm:max-w-2xl">
                    プロジェクトの基本情報、日々の活動記録など、詳細情報を一元的に確認できます。
                </p>
            </div>

            <Tabs defaultValue="basic_info" className="w-full">
                <TabsList
                    className="grid w-full grid-cols-2 gap-1 mb-3 md:mb-8 bg-muted/70 p-1 md:p-1.5 rounded-lg"
                    aria-label="プロジェクト詳細情報カテゴリ"
                >
                    <TabsTrigger value="basic_info" className="data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm rounded-md px-2 py-1.5 md:px-4 md:py-2 text-xs md:text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors duration-150 ease-in-out">基本情報</TabsTrigger>
                    <TabsTrigger value="daily_activities" className="data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm rounded-md px-2 py-1.5 md:px-4 md:py-2 text-xs md:text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors duration-150 ease-in-out">日次活動</TabsTrigger>
                </TabsList>

                <TabsContent value="basic_info">
                    <BasicInfoTab projectData={project} updateProject={updateProject} onProjectUpdated={mutate} />
                </TabsContent>

                <TabsContent value="daily_activities">
                    <DailyActivitiesTab projectId={projectId} />
                </TabsContent>
            </Tabs>
        </div>
    )
}